import { useCallback } from 'react';
import useSchools from './useSchools';
import useBrandSettings from './useBrandSettings';
import { readFreshSelection } from '../services/schoolSelectionTtl';
import { findMenuDestination } from '../services/menuDestination';
import { STORAGE_KEYS } from './useMenuItemNavigation';

/**
 * Visibility of the brand's two fixed header menu items ('menu_1'|'menu_2')
 * under the "Disable Open Shop" brand flag.
 *
 * Open shop ENABLED (default) → an item is visible only when it has its OWN
 * default collection assigned (`menuItems[].hasCollection` from the brand
 * payload) — an unassigned item has no meaningful public listing to land on,
 * so the admin hides it by simply clearing its collection in the Brands form.
 * Items without a collection fall through to the school-based rules below, so
 * a customer whose school has a mapped open shop still sees them.
 *
 * Open shop DISABLED:
 *  - no fresh stored school: selector ON → both visible (clicking opens the
 *    popup); selector OFF → both hidden (school shops are reachable only via
 *    direct URLs).
 *  - stored school: an item is visible only when the school currently has an
 *    open shop mapped to it (`seasons[].menuItemKey` — the schools API returns
 *    only open+active shops). Prefer the live schools list; fall back to the
 *    persisted `school.seasons` while it loads.
 *
 * Loading policy: HIDDEN-first. While brand settings (or the needed schools
 * data) are still loading, the items stay hidden and appear once the data
 * says they should — appearing is less jarring than showing and then
 * disappearing on brands where they end up hidden.
 */
export default function useMenuItemVisibility(brandSlug) {
  const { openShopEnabled, showSchoolSelector, menuItems, loading: brandLoading } = useBrandSettings(brandSlug);
  const { schools, loading: schoolsLoading } = useSchools(brandSlug);

  const isVisible = useCallback((menuKey) => {
    if (brandLoading) return false; // hidden until the flag is known
    // Admin switch (Brands form → "Hide menu item … in the storefront"): off everywhere.
    if ((menuItems || []).find((m) => m.key === menuKey)?.hidden) return false;
    if (openShopEnabled) {
      // Open-shop mode: only items with an ASSIGNED collection get the
      // always-visible treatment. Unassigned items fall through to the
      // school-based rules (a stored school with a mapped open shop still
      // shows them — that click lands on the school shop, not the listing).
      const item = (menuItems || []).find((m) => m.key === menuKey);
      if (item?.hasCollection) return true;
    }

    const stored = readFreshSelection(STORAGE_KEYS[brandSlug]);
    const storedSchool = stored?.school ?? null;

    if (!storedSchool?.slug && !storedSchool?.id) {
      return showSchoolSelector;
    }

    const liveSchool = (schools || []).find(
      (s) => (storedSchool.slug && s.slug === storedSchool.slug)
          || String(s.id) === String(storedSchool.id),
    );
    if (!liveSchool && !schoolsLoading && (schools || []).length) {
      // Stale selection (school gone from the live list) — treat as no school.
      return showSchoolSelector;
    }
    const school = liveSchool ?? storedSchool;
    const seasons = Array.isArray(school?.seasons) ? school.seasons : null;
    if (!seasons && schoolsLoading) return false; // hidden until data arrives
    // Visible only when the school has a destination MAPPED to this item: an
    // open shop with `menuItemKey`, or an open shop with a GROUP ("sub-shop")
    // mapped to it (2026-09-25). Unmapped items hide — a click could only land
    // on some other shop, which is exactly what the user must not get.
    return Boolean(findMenuDestination(school, menuKey).shop);
  }, [openShopEnabled, showSchoolSelector, menuItems, brandLoading, schools, schoolsLoading, brandSlug]);

  /** Admin switch (Brands form): the item must disappear entirely, submenu included. */
  const isHidden = useCallback((menuKey) => Boolean((menuItems || []).find((m) => m.key === menuKey)?.hidden), [menuItems]);

  /**
   * The customer's stored school has NO shop and no group mapped to this item
   * (2026-09-25): the entry must disappear entirely, submenu included — a
   * school that only sells Studieturstrøjer shows no Vogntrøjer item at all.
   * False for anonymous visitors, while data loads, and for stale selections
   * (those keep the anonymous behaviour).
   */
  const isUnmappedForSchool = useCallback((menuKey) => {
    const stored = readFreshSelection(STORAGE_KEYS[brandSlug]);
    const storedSchool = stored?.school ?? null;
    if (!storedSchool?.slug && !storedSchool?.id) return false;
    const liveSchool = (schools || []).find(
      (s) => (storedSchool.slug && s.slug === storedSchool.slug)
          || String(s.id) === String(storedSchool.id),
    );
    if (!liveSchool) return false;
    return !findMenuDestination(liveSchool, menuKey).shop;
  }, [schools, brandSlug]);

  return { isVisible, isHidden, isUnmappedForSchool, openShopEnabled, showSchoolSelector };
}
